import { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../stores/root_store';
import { CollabProps } from '../stores/collab_store';
import List from '@mui/joy/List';

import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import NewIcon from '@mui/icons-material/Add';

import { CollabCreateForm } from './CollabCreate';
import Box from '@mui/joy/Box';
import Tooltip from '@mui/joy/Tooltip';

import { pluralize, getIcon } from 'utils/misc';
import Link from 'components/Link';
import useUserIdentity from 'hooks/useUserIdentity';
import useCollabs from 'hooks/useCollabs';
import useFHRequest from 'hooks/useFHRequest';
import { FHRequestType } from 'stores/fhrequest_store';

import WorkListItem from 'components/WorkListItem';
import { WorkItemType } from 'components/WorkListItem';


type CollabListProps = {
    projectKey: string;
    // onClickNew: () => void;
    // onClickEdit: () => void;
};


export const CollabListBeauty = observer(({ collab }: { collab: CollabProps }) => {
    const DefaultCollabIcon = getIcon('Group');
    // const roles = rootStore.roleStore.getUserRoles(currentUser.key).map((role: RoleProps) => role.role);
    const collaborators = useContext(RootStoreContext).collaboratorsStore.getCollaborators(collab.key || '');

    return (
        <Stack
            flexGrow={1}>
            <Stack
                alignItems="center"
                direction="row"
                spacing={1.5}>

                <Typography level="title-lg"
                    startDecorator={<DefaultCollabIcon />}
                >{collab.title}</Typography>

                <Box flexGrow={1} />

                <Tooltip arrow
                    title="Collabs are requests with more possibility. Billed by time rather than Request credits.">
                    <Chip size="sm">
                        Collab
                    </Chip>
                </Tooltip>
            </Stack>

            <Typography level="body-sm">{collab.description}</Typography>

            {collaborators.length > 1 &&
                <Chip>{pluralize(collaborators.length - 1, 'Collaborator')}</Chip>
            }

        </Stack>

    )
});





function _CollabList({projectKey}: CollabListProps) {
    const { getCollabsByProject, fetchCollabsByProject } = useCollabs();
    const project = useContext(RootStoreContext).projectStore.getProject(projectKey);
    const { currentIdentity } = useUserIdentity();
    const collabs = getCollabsByProject(currentIdentity.uuid, project?.uuid || '');
    const { getListByProject, fetchListByProject } = useFHRequest();
    const [requests, setRequests] = useState<FHRequestType[]>(getListByProject(projectKey));

    const collabsAndRequests = [...collabs, ...requests].sort((a, b) => {
        if(a.date_created === b.date_created) {
            return 0;
        }
        if(a.date_created === undefined) {
            return 1;
        }
        if(b.date_created === undefined) {
            return -1;
        }
        return a.date_created > b.date_created ? -1 : 1;
    }) as (FHRequestType | CollabProps)[];

    useEffect(() => {
        if(project?.uuid && currentIdentity?.uuid) {
            fetchCollabsByProject(currentIdentity.uuid, project.uuid);
            fetchListByProject(projectKey).then(() => {
                setRequests(getListByProject(projectKey));
                console.log("got requests", getListByProject(projectKey));
            });
        }
    }, [fetchCollabsByProject, project?.uuid, currentIdentity?.uuid, fetchListByProject]);


    if (collabsAndRequests.length === 0 && project) {
        return (
            <Stack>
                <CollabCreateForm project={project} />
            </Stack>
        );
    }

    return (
        <div>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                p={2}
                pb={1.5}
                sx={{borderBottom: '1px solid',
                    borderColor: 'divider'}}
            >
                <Typography level="body-sm">
                    This project has <strong>{pluralize(collabs.length, 'collaboration')}</strong> {' '} and <strong>{pluralize(requests.length, 'request')}</strong>.
                </Typography>

                <Link to={"/project/" + projectKey + "/collab/new"}>
                    <Tooltip arrow
                        title="Let's get to work.">
                    <Button
                        variant="outlined"
                        endDecorator={<NewIcon />}
                        color="neutral" size="sm">
                         New
                    </Button>
                    </Tooltip>
                </Link>

            </Stack>

            <List
                sx={{
                    height: "100%",
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    py: 0,
                    '--ListItem-paddingY': '0.75rem',
                    '--ListItem-paddingX': '1rem',
                }}
            >
                { collabsAndRequests.map((item : WorkItemType) => {
                    return <WorkListItem item={item}  />
                })}
            </List>

        </div>
    );

}


const cp = observer(_CollabList);
export default cp;
